import { type Module } from "vuex";
import type {
  BodyPatchEvaluationCheckpoint,
  DefaultApi,
  EvaluateCandidateRequest,
} from "@/api";
import { Evaluation } from "@/models/evaluation";
import { EvaluationRecipientRequest } from "@/models/evaluation_recipient";
import type { AllEvaluations, Evaluations } from "@/models/evaluations";
import type { State as RootState } from "@/stores";
export interface State {
  allEvaluations: AllEvaluations;
  candidateIdsForNavigation: number[] | null;
  evaluationRecipient: EvaluationRecipientRequest[] | null;
}
export const module: Module<State, RootState> = {
  namespaced: true,
  state: {
    allEvaluations: {},
    candidateIdsForNavigation: null,
    evaluationRecipient: null,
  },
  actions: {
    async getEvaluations({ commit }, positionId: number) {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.getEvaluations(positionId);
      const evaluations = Object.fromEntries(
        Object.entries(response.data).map(([evaluationId, evaluation]) => [
          parseInt(evaluationId),
          new Evaluation(evaluation),
        ]),
      );
      commit("setEvaluations", {
        positionId,
        evaluations,
      });
      return evaluations;
    },
    async getEvaluation(
      { commit },
      {
        positionId,
        candidateId,
      }: {
        positionId: number;
        candidateId: number;
      },
    ) {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.getEvaluation(positionId, candidateId);
      const evaluation = new Evaluation(response.data);
      commit("setEvaluation", {
        positionId,
        candidateId,
        evaluation,
      });
      return evaluation;
    },
    async postEvaluationExport(
      { commit },
      {
        positionId,
        candidateId,
        evaluationId,
        skipIntegration,
      }: {
        positionId: number;
        candidateId: number;
        evaluationId: number;
        skipIntegration: boolean;
      },
    ) {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.postEvaluationExport(
        evaluationId,
        positionId,
        candidateId,
        skipIntegration,
      );
      const evaluation = new Evaluation(response.data);
      commit("setEvaluation", {
        positionId,
        candidateId,
        evaluation,
      });
      return evaluation;
    },
    async patchEvaluationCheckpoint(
      { commit },
      {
        positionId,
        candidateId,
        evaluationCheckpointId,
        bodyPatchEvaluationCheckpoint,
      }: {
        positionId: number;
        candidateId: number;
        evaluationCheckpointId: number;
        bodyPatchEvaluationCheckpoint: BodyPatchEvaluationCheckpoint;
      },
    ) {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.patchEvaluationCheckpoint(
        candidateId,
        evaluationCheckpointId,
        positionId,
        bodyPatchEvaluationCheckpoint,
      );
      const evaluation = new Evaluation(response.data);
      commit("setEvaluation", {
        positionId,
        candidateId,
        evaluation,
      });
      return evaluation;
    },
    async patchEvaluation(
      {},
      {
        positionId,
        candidateId,
      }: {
        positionId: number;
        candidateId: number;
      },
    ) {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.patchEvaluation(positionId, candidateId);
      return response;
    },
    async patchEvaluationsBulk(
      {},
      {
        positionId,
        candidateRequests,
      }: {
        positionId: number;
        candidateRequests: EvaluateCandidateRequest[];
      },
    ) {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.patchEvaluationsBulk(
        positionId,
        candidateRequests,
      );
      return response.data;
    },
    async getShareEvaluation(
      { commit },
      {
        positionId,
        candidateId,
      }: {
        positionId: number;
        candidateId: number;
      },
    ) {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.getShareEvaluation(positionId, candidateId);
      const evaluationRecipient = response.data.recipients.map(
        (recipient) => new EvaluationRecipientRequest(recipient),
      );
      commit("setEvaluationRecipient", evaluationRecipient);
      return evaluationRecipient;
    },
    async getEvaluationRecipients(
      { commit },
      {
        positionId,
      }: {
        positionId: number;
      },
    ) {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.getEvaluationRecipients(positionId);
      const evaluationRecipient = response.data.map(
        (recipient) =>
          new EvaluationRecipientRequest({
            ...recipient,
            custom_note: null,
          }),
      );
      commit("setEvaluationRecipient", evaluationRecipient);
      return evaluationRecipient;
    },
    async postShareEvaluation(
      {},
      {
        positionId,
        candidateId,
        evaluationId,
        recipients,
      }: {
        positionId: number;
        candidateId: number;
        evaluationId: number;
        recipients: EvaluationRecipientRequest[];
      },
    ) {
      const api: DefaultApi = await this.dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.postShareEvaluation(
        evaluationId,
        positionId,
        candidateId,
        recipients,
      );
      return response;
    },
  },
  mutations: {
    setEvaluation(
      state,
      {
        positionId,
        candidateId,
        evaluation,
      }: {
        positionId: number;
        candidateId: number;
        evaluation: Evaluation;
      },
    ) {
      const evaluations = Object.assign({}, state.allEvaluations[positionId], {
        [candidateId]: evaluation,
      });
      state.allEvaluations = Object.assign({}, state.allEvaluations, {
        [positionId]: evaluations,
      });
    },
    setEvaluations(
      state,
      {
        positionId,
        evaluations,
      }: {
        positionId: number;
        evaluations: Evaluations;
      },
    ) {
      state.allEvaluations = Object.assign({}, state.allEvaluations, {
        [positionId]: evaluations,
      });
    },
    setCandidateIdsForNavigation(state, candidateIds: number[]) {
      state.candidateIdsForNavigation = candidateIds;
    },
    setEvaluationRecipient(
      state,
      evaluationRecipient: EvaluationRecipientRequest[],
    ) {
      state.evaluationRecipient = evaluationRecipient;
    },
  },
};
